/**
 * Collaborative Puzzle Solving Service
 * Uses Firebase Realtime Database for real-time synchronization
 */

import { ref, set, onValue, off, push, update, remove } from 'firebase/database';
import { realtimeDb } from '../Config/firebase';
import { ChessPuzzle } from './chessPuzzleService';

export interface CollaborativePuzzleSession {
    id: string;
    puzzleId: string;
    puzzle: ChessPuzzle;
    subscriptionId: string;
    createdBy: string;
    createdByName: string;
    createdAt: number;
    participants: {
        [userId: string]: {
            userId: string;
            userName: string;
            userRole: 'teacher' | 'student';
            joinedAt: number;
            boardState: string; // FEN
            moves: string[]; // Array of moves made
            isActive: boolean;
        };
    };
    status: 'active' | 'completed' | 'cancelled';
}

export interface ParticipantBoardState {
    userId: string;
    userName: string;
    userRole: 'teacher' | 'student';
    boardState: string; // FEN
    moves: string[];
    isActive: boolean;
    isPuzzleSolved?: boolean; // Whether the participant has solved the puzzle
    solvedAt?: number; // Timestamp when puzzle was solved
}

/**
 * Create a new collaborative puzzle session
 */
export const createCollaborativeSession = async (
    puzzle: ChessPuzzle,
    subscriptionId: string,
    userId: string,
    userName: string,
    userRole: 'teacher' | 'student'
): Promise<string> => {
    try {
        console.log('🆕 [SMP] createCollaborativeSession: Starting', { 
            subscriptionId, 
            userId, 
            userName,
            userRole,
            puzzleId: puzzle.id,
            dbPath: `collaborativePuzzles/${subscriptionId}`
        });
        
        if (!subscriptionId || !userId || !puzzle) {
            throw new Error('Missing required parameters: subscriptionId, userId, or puzzle');
        }
        
        // Check if an active session already exists for this puzzle
        const sessionsRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}`);
        const snapshot = await new Promise<any>((resolve) => {
            onValue(sessionsRef, (snap) => {
                resolve(snap);
            }, { onlyOnce: true });
        });
        
        const existingSessions = snapshot.val();
        if (existingSessions) {
            // Look for an active session with the same puzzle
            for (const [sessionId, session] of Object.entries(existingSessions)) {
                const sess = session as CollaborativePuzzleSession;
                if (sess.puzzleId === puzzle.id && sess.status === 'active') {
                    console.log('✅ Found existing active session for this puzzle:', sessionId);
                    
                    // Check if user is already a participant
                    if (!sess.participants[userId]) {
                        // Add the user as a participant to the existing session
                        const participantRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}/participants/${userId}`);
                        await set(participantRef, {
                            userId,
                            userName,
                            userRole,
                            joinedAt: Date.now(),
                            boardState: puzzle.fen,
                            moves: [],
                            isActive: true
                        });
                        console.log('✅ User added to existing session:', sessionId);
                    } else {
                        console.log('✅ User already in session:', sessionId);
                    }
                    
                    return sessionId;
                }
            }
        }
        
        // No existing active session found, create a new one
        const sessionRef = push(ref(realtimeDb, `collaborativePuzzles/${subscriptionId}`));
        const sessionId = sessionRef.key!;
        
        if (!sessionId) {
            throw new Error('Failed to generate session ID');
        }
        
        console.log('createCollaborativeSession: Generated new session ID', sessionId);
        
        const session: CollaborativePuzzleSession = {
            id: sessionId,
            puzzleId: puzzle.id,
            puzzle,
            subscriptionId,
            createdBy: userId,
            createdByName: userName,
            createdAt: Date.now(),
            participants: {
                [userId]: {
                    userId,
                    userName,
                    userRole,
                    joinedAt: Date.now(),
                    boardState: puzzle.fen,
                    moves: [],
                    isActive: true
                }
            },
            status: 'active'
        };
        
        console.log('createCollaborativeSession: Writing session to database', { sessionId, path: `collaborativePuzzles/${subscriptionId}/${sessionId}` });
        
        await set(sessionRef, session);
        
        console.log('createCollaborativeSession: Session written successfully', sessionId);
        
        return sessionId;
    } catch (error) {
        console.error('createCollaborativeSession: Error creating session', error);
        throw error;
    }
};

/**
 * Join an existing collaborative puzzle session
 * Maximum 10 students allowed per session (excluding teacher)
 */
export const joinCollaborativeSession = async (
    sessionId: string,
    subscriptionId: string,
    userId: string,
    userName: string,
    userRole: 'teacher' | 'student'
): Promise<void> => {
    const sessionRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}`);
    const participantRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}/participants/${userId}`);
    
    // Maximum students allowed (excluding teacher)
    const MAX_STUDENTS = 10;
    
    // Get current session to get initial puzzle FEN and check participant count
    return new Promise((resolve, reject) => {
        onValue(sessionRef, (snapshot) => {
            const session = snapshot.val() as CollaborativePuzzleSession;
            if (session && session.puzzle) {
                // Check if user is already a participant
                if (session.participants && session.participants[userId]) {
                    // Already a participant, just resolve
                    off(sessionRef);
                    resolve();
                    return;
                }
                
                // Count active students (excluding teachers)
                const activeStudents = Object.values(session.participants || {}).filter(
                    p => p.userRole === 'student' && p.isActive
                ).length;
                
                // Check if adding a new student would exceed the limit
                if (userRole === 'student' && activeStudents >= MAX_STUDENTS) {
                    off(sessionRef);
                    reject(new Error(`Session is full. Maximum ${MAX_STUDENTS} students allowed.`));
                    return;
                }
                
                const participantData = {
                    userId,
                    userName,
                    userRole,
                    joinedAt: Date.now(),
                    boardState: session.puzzle.fen,
                    moves: [],
                    isActive: true
                };
                
                set(participantRef, participantData)
                    .then(() => {
                        off(sessionRef);
                        resolve();
                    })
                    .catch(reject);
            } else {
                off(sessionRef);
                reject(new Error('Session not found'));
            }
        }, { onlyOnce: true });
    });
};

/**
 * Update a participant's board state
 */
export const updateParticipantBoardState = (
    sessionId: string,
    subscriptionId: string,
    userId: string,
    boardState: string,
    moves: string[],
    isPuzzleSolved?: boolean
): void => {
    const participantRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}/participants/${userId}`);
    const updateData: any = {
        boardState,
        moves,
        lastUpdated: Date.now()
    };
    
    if (isPuzzleSolved !== undefined) {
        updateData.isPuzzleSolved = isPuzzleSolved;
        if (isPuzzleSolved) {
            updateData.solvedAt = Date.now();
        }
    }
    
    update(participantRef, updateData);
};

/**
 * Switch the active puzzle for an existing collaborative session.
 * Updates the session's puzzle and resets all participants' board state.
 * Uses set(merge) on the session ref so all listeners (teacher + students) receive the new puzzle.
 */
export const switchSessionPuzzle = async (
    sessionId: string,
    subscriptionId: string,
    puzzle: ChessPuzzle
): Promise<void> => {
    const sessionRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}`);

    // Load current session once
    const snapshot = await new Promise<any>((resolve) => {
        onValue(
            sessionRef,
            (snap) => {
                resolve(snap);
            },
            { onlyOnce: true }
        );
    });

    const session = snapshot.val() as CollaborativePuzzleSession | null;
    if (!session) {
        throw new Error('Session not found');
    }

    // Plain object with no undefined values (Realtime DB rejects undefined)
    const puzzlePayload: Record<string, unknown> = {
        id: puzzle.id,
        fen: puzzle.fen,
        moves: puzzle.moves,
        rating: puzzle.rating,
        themes: puzzle.themes || [],
        description: puzzle.description ?? '',
        hint: puzzle.hint ?? '',
        complexity: puzzle.complexity,
        category: puzzle.category,
        source: puzzle.source
    };
    if (puzzle.lichessId != null) {
        puzzlePayload.lichessId = puzzle.lichessId;
    }

    // 1. Update session puzzle on the session ref so onValue(sessionRef) fires for all clients
    await set(sessionRef, {
        ...session,
        puzzleId: puzzle.id,
        puzzle: puzzlePayload
    });

    // 2. Reset each participant's board state (so students start from new FEN)
    if (session.participants) {
        const participantUpdates: Record<string, unknown> = {};
        Object.keys(session.participants).forEach((participantId) => {
            participantUpdates[participantId] = {
                ...session.participants[participantId],
                boardState: puzzle.fen,
                moves: [],
                isPuzzleSolved: false,
                solvedAt: null
            };
        });
        const participantsRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}/participants`);
        await set(participantsRef, participantUpdates);
    }
};

/**
 * Listen to session changes (for teacher to see all participants)
 */
export const subscribeToSession = (
    sessionId: string,
    subscriptionId: string,
    callback: (session: CollaborativePuzzleSession | null) => void
): (() => void) => {
    const sessionRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}`);
    
    onValue(sessionRef, (snapshot) => {
        const session = snapshot.val() as CollaborativePuzzleSession | null;
        callback(session);
    });
    
    // Return unsubscribe function
    return () => {
        off(sessionRef);
    };
};

/**
 * Listen to a specific participant's board state
 */
export const subscribeToParticipant = (
    sessionId: string,
    subscriptionId: string,
    userId: string,
    callback: (participant: ParticipantBoardState | null) => void
): (() => void) => {
    const participantRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}/participants/${userId}`);
    
    onValue(participantRef, (snapshot) => {
        const participant = snapshot.val() as ParticipantBoardState | null;
        callback(participant);
    });
    
    // Return unsubscribe function
    return () => {
        off(participantRef);
    };
};

/**
 * Listen to the participants list (for teacher to detect new joins in real-time)
 */
export const subscribeToParticipantsList = (
    sessionId: string,
    subscriptionId: string,
    callback: (participants: { [userId: string]: ParticipantBoardState } | null) => void
): (() => void) => {
    const participantsRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}/participants`);
    
    console.log('🔔 [SMP] Setting up participants list listener:', {
        path: `collaborativePuzzles/${subscriptionId}/${sessionId}/participants`,
        sessionId,
        subscriptionId
    });
    
    onValue(participantsRef, (snapshot) => {
        const participants = snapshot.val() as { [userId: string]: ParticipantBoardState } | null;
        console.log('🔔 [SMP] Participants list snapshot received:', {
            exists: snapshot.exists(),
            hasChildren: snapshot.hasChildren(),
            keys: participants ? Object.keys(participants) : [],
            count: participants ? Object.keys(participants).length : 0,
            participants: participants ? Object.entries(participants).map(([id, p]) => ({
                id,
                userName: p.userName,
                userRole: p.userRole,
                isActive: p.isActive,
                moves: p.moves?.length || 0
            })) : []
        });
        callback(participants);
    }, (error) => {
        console.error('❌ [SMP] Error in participants list subscription:', error);
    });
    
    // Return unsubscribe function
    return () => {
        console.log('🔕 [SMP] Unsubscribing from participants list');
        off(participantsRef);
    };
};

/**
 * Mark participant as inactive (when they leave)
 */
export const leaveCollaborativeSession = (
    sessionId: string,
    subscriptionId: string,
    userId: string
): void => {
    const participantRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}/participants/${userId}`);
    update(participantRef, {
        isActive: false,
        leftAt: Date.now()
    });
};

/**
 * End/close a collaborative session
 */
export const endCollaborativeSession = (
    sessionId: string,
    subscriptionId: string
): void => {
    const sessionRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}`);
    update(sessionRef, {
        status: 'completed',
        endedAt: Date.now()
    });
};

/**
 * Delete a collaborative session completely (removes from database)
 * This releases storage and should only be called when teacher leaves
 */
export const deleteCollaborativeSession = async (
    sessionId: string,
    subscriptionId: string
): Promise<void> => {
    const sessionRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}/${sessionId}`);
    await remove(sessionRef);
};

/**
 * Get all active sessions for a subscription
 */
export const getActiveSessions = (
    subscriptionId: string,
    callback: (sessions: { [sessionId: string]: CollaborativePuzzleSession }) => void
): (() => void) => {
    const sessionsRef = ref(realtimeDb, `collaborativePuzzles/${subscriptionId}`);
    
    onValue(sessionsRef, (snapshot) => {
        const sessions = snapshot.val() as { [sessionId: string]: CollaborativePuzzleSession } | null;
        if (sessions) {
            // Filter only active sessions
            const activeSessions: { [sessionId: string]: CollaborativePuzzleSession } = {};
            Object.entries(sessions).forEach(([id, session]) => {
                if (session.status === 'active') {
                    activeSessions[id] = session;
                }
            });
            callback(activeSessions);
        } else {
            callback({});
        }
    });
    
    // Return unsubscribe function
    return () => {
        off(sessionsRef);
    };
};

/**
 * Get a session by sessionId (searches across all subscriptions)
 * This allows loading a session with just the sessionId
 */
export const getSessionById = (
    sessionId: string,
    callback: (session: CollaborativePuzzleSession | null, subscriptionId: string | null) => void
): (() => void) => {
    // Search in the collaborativePuzzles root
    const rootRef = ref(realtimeDb, 'collaborativePuzzles');
    
    onValue(rootRef, (snapshot) => {
        const allSessions = snapshot.val();
        if (!allSessions) {
            callback(null, null);
            return;
        }
        
        // Search through all subscriptions
        for (const [subscriptionId, sessions] of Object.entries(allSessions)) {
            if (sessions && typeof sessions === 'object') {
                const session = (sessions as any)[sessionId];
                if (session && session.id === sessionId) {
                    callback(session as CollaborativePuzzleSession, subscriptionId);
                    return;
                }
            }
        }
        
        // Session not found
        callback(null, null);
    });
    
    // Return unsubscribe function
    return () => {
        off(rootRef);
    };
};
